<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'

import BattleStage3D from './BattleStage3D.vue'
import { eventText } from './format.js'
import type { BattleEvent } from './types.js'

interface UnitView {
  id: string
  name: string
  initials: string
  faction: 'ember' | 'tide' | 'grove'
  side: string
  role: 'hero' | 'creep'
  health: number
  maxHealth: number
  position: number
  alive: boolean
}

const props = defineProps<{ events: BattleEvent[]; mode: 'raid' | 'arena' }>()
const units = reactive<Record<string, UnitView>>({})
const bases = reactive({ blue: 1500, red: 1500 })
const eventIndex = ref(0)
const playing = ref(false)
const speed = ref(280)
const messages = ref<string[]>([])
const graphicsStatus = ref<'loading' | 'ready' | 'failed'>('loading')
const visualRevision = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

const unitList = computed(() => Object.values(units))
const progress = computed(() => props.events.length === 0 ? 0 : Math.round(eventIndex.value / props.events.length * 100))
const result = computed(() => {
  for (let index = props.events.length - 1; index >= 0; index -= 1) {
    const event = props.events[index]
    if (event?.type === 'battle_ended') return event
  }
  return undefined
})

function stop(): void {
  playing.value = false
  if (timer !== null) clearInterval(timer)
  timer = null
}

function apply(event: BattleEvent): void {
  if (event.type === 'unit_spawned') {
    units[event.unitId] = {
      id: event.unitId,
      name: event.name,
      initials: event.initials,
      faction: event.faction,
      side: event.side,
      role: event.role,
      health: event.health,
      maxHealth: event.maxHealth,
      position: event.position ?? (event.side === 'player' ? 20 : 80),
      alive: true,
    }
  } else if (event.type === 'attack') {
    const target = units[event.targetId]
    if (target) target.health = event.targetHealth
  } else if (event.type === 'unit_died') {
    const unit = units[event.unitId]
    if (unit) unit.alive = false
  } else if (event.type === 'unit_moved') {
    const unit = units[event.unitId]
    if (unit) unit.position = event.to
  } else if (event.type === 'base_damaged') {
    bases[event.side] = event.health
  }
  if (event.type !== 'unit_moved' && event.type !== 'battle_started') {
    messages.value = [...messages.value.slice(-5), eventText(event)]
  }
}

function advanceTick(): void {
  if (eventIndex.value >= props.events.length) {
    stop()
    return
  }
  const tick = props.events[eventIndex.value]?.tick
  while (eventIndex.value < props.events.length && props.events[eventIndex.value]?.tick === tick) {
    const event = props.events[eventIndex.value]
    if (event) apply(event)
    eventIndex.value += 1
  }
  if (eventIndex.value >= props.events.length) stop()
}

function play(): void {
  if (playing.value || eventIndex.value >= props.events.length) return
  playing.value = true
  timer = setInterval(advanceTick, speed.value)
}

function reset(): void {
  stop()
  visualRevision.value += 1
  for (const key of Object.keys(units)) delete units[key]
  bases.blue = 1500
  bases.red = 1500
  eventIndex.value = 0
  messages.value = []
}

function restart(): void {
  reset()
  advanceTick()
  play()
}

function changeSpeed(): void {
  if (!playing.value) return
  stop()
  play()
}

function skip(): void {
  stop()
  while (eventIndex.value < props.events.length) advanceTick()
}

function tokenStyle(unit: UnitView, index: number): Record<string, string> {
  if (props.mode === 'raid') return {}
  const row = unit.role === 'hero' ? index % 3 : 3 + index % 2
  return { left: `calc(${unit.position}% - 18px)`, top: `${24 + row * 34}px` }
}

watch(() => props.events, (current, previous) => {
  const isContinuation = previous !== undefined
    && previous.length > 0
    && current.length >= previous.length
    && previous.every((event, index) => current[index]?.sequence === event.sequence)
  if (!isContinuation) {
    reset()
    if (current.length > 0) advanceTick()
  }
  if (current.length > eventIndex.value) play()
}, { immediate: true })

onBeforeUnmount(stop)
</script>

<template>
  <section class="battle-player">
    <div class="playback-head">
      <strong>Запись боя · {{ progress }}%</strong>
      <div class="playback-controls">
        <select v-model.number="speed" aria-label="Скорость записи" @change="changeSpeed">
          <option :value="600">×0.5</option>
          <option :value="280">×1</option>
          <option :value="100">×3</option>
        </select>
        <button class="tiny" type="button" @click="playing ? stop() : play()">
          {{ playing ? 'Пауза' : 'Играть' }}
        </button>
        <button class="tiny" type="button" @click="restart">Сначала</button>
        <button class="tiny" type="button" @click="skip">Итог</button>
      </div>
    </div>

    <div class="battlefield-viewport">
      <BattleStage3D
        v-if="graphicsStatus !== 'failed'"
        class="stage-host"
        :class="{ ready: graphicsStatus === 'ready' }"
        :events="events"
        :processed-count="eventIndex"
        :mode="mode"
        :speed-ms="speed"
        :revision="visualRevision"
        @ready="graphicsStatus = 'ready'"
        @failed="graphicsStatus = 'failed'"
      />

      <div v-if="mode === 'raid'" v-show="graphicsStatus !== 'ready'" class="raid-field">
        <div class="raid-side">
          <span class="side-label">Твой отряд</span>
          <div class="unit-row">
            <div
              v-for="unit in unitList.filter(item => item.side === 'player')"
              :key="unit.id"
              class="unit"
              :class="[{ dead: !unit.alive }, `faction-${unit.faction}`]"
            >
              <span>{{ unit.initials }}</span>
              <i :style="{ width: `${Math.max(0, unit.health / unit.maxHealth * 100)}%` }" />
            </div>
          </div>
        </div>
        <div class="versus">VS</div>
        <div class="raid-side">
          <span class="side-label">Враги</span>
          <div class="unit-row">
            <div
              v-for="unit in unitList.filter(item => item.side === 'enemy')"
              :key="unit.id"
              class="unit"
              :class="[{ dead: !unit.alive }, `faction-${unit.faction}`]"
            >
              <span>{{ unit.initials }}</span>
              <i :style="{ width: `${Math.max(0, unit.health / unit.maxHealth * 100)}%` }" />
            </div>
          </div>
        </div>
      </div>

      <div v-else v-show="graphicsStatus !== 'ready'" class="arena-field">
        <div class="base base-blue"><b>Б</b><small>{{ bases.blue }}</small></div>
        <div class="lane" />
        <div class="base base-red"><b>Б</b><small>{{ bases.red }}</small></div>
        <div
          v-for="(unit, index) in unitList"
          :key="unit.id"
          class="arena-unit"
          :class="[{ dead: !unit.alive, creep: unit.role === 'creep' }, `faction-${unit.faction}`]"
          :style="tokenStyle(unit, index)"
          :title="unit.name"
        >
          {{ unit.initials }}
          <i :style="{ width: `${Math.max(0, unit.health / unit.maxHealth * 100)}%` }" />
        </div>
      </div>
    </div>

    <div class="event-feed" aria-live="polite">
      <p v-for="(message, index) in messages" :key="`${index}-${message}`">{{ message }}</p>
      <p v-if="messages.length === 0" class="muted">Бой готов к воспроизведению.</p>
    </div>
    <p v-if="result && eventIndex >= events.length" class="battle-result">
      {{ eventText(result) }}
    </p>
  </section>
</template>

<style scoped>
.battle-player { display: grid; gap: 10px; }
.playback-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
.playback-controls { display: flex; gap: 5px; align-items: center; flex-wrap: wrap; }
.playback-controls select, .tiny { padding: 7px; color: #fff; background: #272e4d; border: 0; border-radius: 8px; }
.battlefield-viewport { position: relative; overflow: hidden; inline-size: 100%; block-size: clamp(240px, 68vw, 280px); border-radius: 16px; isolation: isolate; }
.stage-host { visibility: hidden; opacity: 0; transition: opacity .18s ease; }
.stage-host.ready { visibility: visible; opacity: 1; }
.battlefield-viewport > .raid-field, .battlefield-viewport > .arena-field { position: absolute; inset: 0; block-size: 100%; }
.raid-field { display: grid; gap: 10px; padding: 12px; border-radius: 16px; background: #101426; }
.raid-side { display: grid; gap: 6px; }
.side-label { color: #9fa8c8; font-size: 12px; }
.unit-row { display: flex; justify-content: center; gap: 9px; min-block-size: 48px; }
.unit, .arena-unit { position: relative; display: grid; place-items: center; inline-size: 42px; aspect-ratio: 1; border-radius: 50%; font-size: 12px; font-weight: 900; box-shadow: 0 3px 10px #0008, inset 0 0 0 2px #fff5; transition: left .22s linear, top .22s linear, opacity .2s, transform .2s; }
.unit i, .arena-unit i { position: absolute; inset-inline-start: 4px; inset-block-end: -5px; block-size: 4px; max-inline-size: calc(100% - 8px); background: #70ed9f; border-radius: 4px; transition: width .15s; }
.dead { opacity: .2; transform: scale(.7); filter: grayscale(1); }
.versus { color: #747e9e; font-size: 11px; font-weight: 900; text-align: center; }
.arena-field { position: relative; overflow: hidden; inline-size: 100%; block-size: 220px; border-radius: 16px; background: linear-gradient(180deg, #15243d, #172e26); }
.lane { position: absolute; inset-inline: 7%; inset-block-start: 50%; block-size: 9px; transform: translateY(-50%); background: #747b72; border-block: 2px dashed #aab09f; }
.base { position: absolute; inset-block: 78px auto; z-index: 3; display: grid; place-items: center; inline-size: 42px; block-size: 64px; border-radius: 10px 10px 4px 4px; }
.base small { font-size: 9px; }
.base-blue { inset-inline-start: 2%; background: #316cbf; }
.base-red { inset-inline-end: 2%; background: #b84450; }
.arena-unit { position: absolute; z-index: 4; inline-size: 36px; }
.arena-unit.creep { inline-size: 28px; font-size: 9px; border-radius: 8px; }
.event-feed { min-block-size: 116px; padding: 10px; border-radius: 12px; background: #111528; }
.event-feed p { margin: 3px 0; font-size: 12px; }
.battle-result { margin: 0; padding: 10px; border-radius: 10px; background: #3a326e; text-align: center; font-weight: 800; }
.faction-ember { background: #d9524f; }
.faction-tide { background: #3479c9; }
.faction-grove { background: #379765; }
</style>
